import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Chip } from '@heroui/react'
import { api } from '../lib/api'
import { CHANNEL_LABEL, deliveryTarget, failedCount, failureText, resendNote, resendThenReload, STATUS_LABEL } from '../lib/deliveries'
import { explain } from '../lib/staffErrors'
import { formatMoment } from '../lib/staffText'
import type { Delivery, DeliveryChannel, DeliveryStatus } from '../lib/types'
import { Icon } from '../ui/icons'
import { EmptyState } from '../ui/parts'
import { useStaffText } from '../ui/StaffText'
import { useFlash } from './sectionKit'
import { ListCard, ListRow, ListTable, PageHead } from './settingsKit'
import '../styles/connect.css'

/**
 * Deliveries: what the outbox tried to send, on every channel, and whether it
 * arrived — the one place a receiver or a gateway that fails silently is seen.
 * Read on demand (Refresh) rather than live: it is a place somebody comes to
 * when asked "did the CRM get it?", not a board anybody watches.
 *
 * The filters are the server's (it keeps the newest 200 of each), so a failure
 * older than the last 200 messages still shows once "Failed" is chosen.
 */

/** The local calendar day a moment falls on, as a key to group rows by. */
const dayKey = (at: string) => {
  const d = new Date(at)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

/** The installation owner also sees webhooks and sends again; an organisation's
 *  administrator sees its own messages only. Which one is the server's to say. */
export function Deliveries({ organisation }: { organisation?: string }) {
  const { lang, say } = useStaffText()
  const [owner, setOwner] = useState(false)
  const { flash, toast } = useFlash()
  const [rows, setRows] = useState<Delivery[]>([])
  const [loaded, setLoaded] = useState(false)
  const [readAt, setReadAt] = useState<Date | null>(null)
  const [channel, setChannel] = useState<'' | DeliveryChannel>('')
  const [status, setStatus] = useState<'' | DeliveryStatus>('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  // Each load is numbered, and only the latest may write: changing the filter
  // twice quickly, or Refresh during a slow answer, must not let an earlier
  // answer land last and show a list for a filter no longer chosen.
  const latest = useRef(0)

  const load = useCallback(async () => {
    const mine = ++latest.current
    setBusy(true)
    try {
      const { deliveries, owner: seesAll } = await api.deliveries(organisation, { channel, status })
      if (mine !== latest.current) return
      setRows(deliveries)
      setOwner(seesAll === true)
      setLoaded(true)
      setReadAt(new Date())
      setError('')
    } catch (err) {
      if (mine !== latest.current) return
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      if (mine === latest.current) setBusy(false)
    }
    // lang deliberately not a dependency: a language switch must not refetch.
  }, [organisation, channel, status])

  useEffect(() => {
    void load()
  }, [load])

  const resend = async (ids: string[]) => {
    setError('')
    setBusy(true)
    const { sent, refusal } = await resendThenReload(ids, (id) => api.resendDelivery(organisation, id), load)
    if (sent > 0) flash(sent === 1 ? say('connect.sendingAgain') : say('connect.sendingN', { n: sent }))
    // After the reload, which clears the error: the refusal stays until dismissed or the next action.
    if (refusal) setError(explain(refusal, lang, 'common.unreachable'))
  }

  const failed = failedCount(rows)
  const resendable = rows.filter((d) => d.status === 'failed' && d.canResend)

  const today = dayKey(new Date().toISOString())
  const yesterday = dayKey(new Date(Date.now() - 86_400_000).toISOString())
  const dayName = (at: string) => {
    const k = dayKey(at)
    if (k === today) return say('connect.today')
    if (k === yesterday) return say('connect.yesterday')
    return formatMoment(at, lang, { date: true, weekday: true })
  }
  // Newest first, as the server sends them; a day starts where the last ended.
  const days: { key: string; name: string; rows: Delivery[] }[] = []
  for (const d of rows) {
    const k = dayKey(d.createdAt)
    const last = days[days.length - 1]
    if (last && last.key === k) last.rows.push(d)
    else days.push({ key: k, name: dayName(d.createdAt), rows: [d] })
  }

  const time = (at: string) => formatMoment(at, lang, { time: true })

  const channels = owner ? (['', 'webhook', 'sms', 'email'] as const) : (['', 'sms', 'email'] as const)

  return (
    <>
      <PageHead
        title={say('admin.section.deliveries')}
        line={say('connect.line.deliveries')}
        actions={
          <>
            {readAt && <span className="set-muted">{say('connect.log.readAt', { time: formatMoment(readAt, lang, { time: true }) })}</span>}
            <Button variant="secondary" isDisabled={busy} onPress={() => void load()}>
              <Icon name="refresh" size={16} /> {say('deliveries.refresh')}
            </Button>
          </>
        }
      />

      {error && (
        <Alert status="danger" role="alert" className="connect-gap connect-dismiss">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Description>{error}</Alert.Description>
          </Alert.Content>
          <Button isIconOnly variant="ghost" size="sm" aria-label={say('ui.close')} onPress={() => setError('')}>
            <Icon name="close" size={16} />
          </Button>
        </Alert>
      )}

      {failed > 0 && (
        <Alert status="warning" className="connect-failbar connect-gap">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>{say('connect.failedN', { n: failed })}</Alert.Title>
            <Alert.Description>
              {resendable.length > 0 ? say('connect.canResend', { n: resendable.length }) : owner && say('connect.noneResend')}
            </Alert.Description>
          </Alert.Content>
          {resendable.length > 1 && (
            <Button variant="secondary" size="sm" isDisabled={busy} onPress={() => void resend(resendable.map((d) => d.id))}>
              {say('connect.sendAllAgain', { n: resendable.length })}
            </Button>
          )}
        </Alert>
      )}

      <ListCard
        title={say('connect.log.list')}
        count={loaded ? say('connect.log.count', { n: rows.length }) : undefined}
        chipGroups={[
          {
            label: say('deliveries.state'),
            chips: (['', 'failed', 'pending', 'delivered'] as const).map((s) => ({
              value: s,
              label: s ? say(STATUS_LABEL[s]) : say('connect.any'),
            })),
            chip: status,
            onChip: (s) => setStatus(s as '' | DeliveryStatus),
          },
          {
            label: say('deliveries.kind'),
            chips: channels.map((c) => ({ value: c, label: c ? say(CHANNEL_LABEL[c]) : say('connect.all') })),
            chip: channel,
            onChip: (c) => setChannel(c as '' | DeliveryChannel),
          },
        ]}
      >
        {!loaded && !error && <p className="empty">{say('common.loading')}</p>}
        {loaded && rows.length === 0 && <EmptyState icon={<Icon name="mail" size={20} />} title={say('deliveries.none')} />}
        {rows.length > 0 && (
          <ListTable
            label={say('connect.log.list')}
            cols="72px minmax(0, 1.2fr) minmax(0, 1fr) minmax(88px, auto)"
            head={[say('deliveries.when'), say('connect.message'), say('deliveries.state'), <span className="sr-only">{say('deliveries.resend')}</span>]}
          >
            {days.map((day) => (
              <Fragment key={day.key}>
                <div className="connect-log__day" role="row">
                  <span role="rowheader">{day.name}</span>
                </div>
                {day.rows.map((d) => {
                  const target = deliveryTarget(d, lang)
                  // An email's subject; otherwise whom it was for (a webhook's body is not kept here, a text's is personal).
                  const about = d.subject || target
                  const gone = (d.channel === 'webhook' && d.endpointGone) || (d.personal && !d.target)
                  return (
                    <ListRow key={d.id}>
                      <span className="connect-log__when" role="cell">
                        {time(d.createdAt)}
                      </span>
                      <span className="connect-log__what" role="cell">
                        <span className="connect-log__about">{about}</span>
                        <span className="connect-log__to">
                          <Chip size="sm" variant="secondary">
                            {say(CHANNEL_LABEL[d.channel])}
                          </Chip>
                          {d.subject && (gone ? <em>{target}</em> : <span>{target}</span>)}
                        </span>
                      </span>
                      <span className="connect-log__state" role="cell">
                        <span className="connect-st" data-state={d.status}>
                          {d.status === 'delivered' &&
                            (d.deliveredAt ? say('connect.deliveredAt', { time: time(d.deliveredAt) }) : say('deliveries.status.delivered'))}
                          {d.status === 'pending' &&
                            (d.nextAttemptAt ? say('connect.nextTry', { time: time(d.nextAttemptAt) }) : say('deliveries.status.pending'))}
                          {d.status === 'failed' && say('deliveries.status.failed')}
                        </span>
                        {(d.attempts > 1 || d.status !== 'delivered') && (
                          <span className="connect-log__tries">{say('connect.attempts', { n: d.attempts })}</span>
                        )}
                        {/* A delivered row can carry a note too (recipients refused for good). */}
                        {d.lastError && <span className="connect-log__err">{failureText(d.lastError, lang)}</span>}
                        {d.status === 'failed' && resendNote(d, lang) && <span className="connect-log__tries">{resendNote(d, lang)}</span>}
                      </span>
                      <span className="connect-rowact" role="cell">
                        {d.canResend && (
                          <Button variant="secondary" size="sm" isDisabled={busy} onPress={() => void resend([d.id])}>
                            {say('deliveries.resend')}
                          </Button>
                        )}
                      </span>
                    </ListRow>
                  )
                })}
              </Fragment>
            ))}
          </ListTable>
        )}
      </ListCard>
      {toast}
    </>
  )
}
